import React, { useState } from "react";
// UI Framework
import { Menu, MenuItem, Tooltip } from "@mui/material";
// Common Components
import Button from "src/Components/Button";
import DatePicker from "src/Components/DatePicker";
import FullScreenLoading from "src/Components/Loading/FullScreenLoading";
import { Modal } from "src/Components";
import { WrappedTimeSeriesChart } from "src/Components/Timeseries/WrappedTimeSeriesChart";
import Cell from "src/Tables/TableTemplate/Cell";
// Requests
import { fundCancelNavGraph } from "src/Requests/goldArbitrage";
// Utilities
import { packetErrorHandler } from "src/Utils/requestUtils";
// Types
import { Graph_Dto } from "src/Interfaces/orderbookGraphInterfaces";
import { IFundCancelNavDailyGraph } from "src/Interfaces/goldArbitrage";

interface IProps {
  headerName: string;
  row: any;
}

function getDateFromToday(dayCount: number) {
  const targetDate = new Date();
  targetDate.setDate(new Date().getDate() - dayCount);
  return targetDate;
}

const CancelNavGraph = ({ headerName, row }: IProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [loading, setLoading] = React.useState(false);
  const [graphData, setGraphData] = React.useState<Graph_Dto[]>([]);
  const [fromDate, setFromDate] = React.useState(
    getDateFromToday(60).toISOString(),
  );
  const [toDate, setToDate] = React.useState(new Date().toISOString());

  React.useEffect(() => {
    if (!isModalOpen) return;
    onSubmit();
  }, [isModalOpen]);

  const handleFromDate = (_: string, value: string) => {
    setFromDate(value);
  };

  const handleToDate = (_: string, value: string) => {
    setToDate(value);
  };

  const formatDate = (date: string) => date?.split("T")[0] ?? date;

  const onSubmit = () => {
    setLoading(true);
    const data: IFundCancelNavDailyGraph = {
      fromDate: formatDate(fromDate),
      toDate: formatDate(toDate),
      instrumentId: row.instrumentId ?? "",
    };

    fundCancelNavGraph(data)
      .then((resp: Graph_Dto | Graph_Dto[]) => {
        setGraphData(Array.isArray(resp) ? resp : resp ? [resp] : []);
      })
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  };

  const setDateFromBtns = (dayCount: number) => {
    const today = new Date();
    const targetDate = getDateFromToday(dayCount);
    setFromDate(targetDate.toISOString());
    setToDate(today.toISOString());
  };

  const getDateSetterBtn = (dayCount: number) => (
    <Button
      text={`${dayCount} روز`}
      onClick={() => setDateFromBtns(dayCount)}
      variant={
        fromDate?.split("T")[0] ===
        getDateFromToday(dayCount).toISOString()?.split("T")[0]
          ? "contained"
          : "outlined"
      }
    />
  );

  const handleCloseMenu = () => {
    setIsMenuOpen(false);
    setAnchorEl(null);
    setIsTooltipOpen(false);
  };

  const rightClickHandler = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsMenuOpen(true);
    setAnchorEl(e.currentTarget);
  };

  const getChartHandler = () => {
    setIsModalOpen(true);
  };

  const ModalUI = () => {
    return (
      <div className="w-full flex flex-col justify-start items-start gap-3 px-16">
        <span className="font-bold">NAV ابطال {row.name ?? ""}</span>
        <div className="w-full flex flex-row justify-start items-start gap-5">
          {loading ? <FullScreenLoading /> : <></>}
          <div className="flex shrink-0 flex-col justify-start items-start gap-3">
            <DatePicker
              label="از تاریخ"
              name="fromDate"
              onChange={handleFromDate}
              value={fromDate}
            />
            <DatePicker
              label="تا تاریخ"
              name="toDate"
              onChange={handleToDate}
              value={toDate}
            />
            <div className="flex flex-row justify-start items-center gap-1 w-full">
              {getDateSetterBtn(7)}
              {getDateSetterBtn(30)}
              {getDateSetterBtn(60)}
            </div>
            <Button text="جستجو" onClick={onSubmit} variant="contained" />
          </div>
          <div className="chart-fill-modal font-bold">
            <WrappedTimeSeriesChart
              dataset={graphData}
              withDate
              YValueType="priceWithDecimal"
            className="h-full w-full" />
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {isModalOpen ? (
        <Modal
          onClose={() => {
            handleCloseMenu();
            setIsModalOpen(false);
          }}
          fullScreen
          title={`NAV ابطال ${row.name ?? ""}`.trim()}
          UI={ModalUI()}
          modalBoxClassName="!z-[9999]"
        />
      ) : (
        <></>
      )}
      <Tooltip
        title="نمودار"
        className="!w-full !relative"
        open={isTooltipOpen}
        onMouseEnter={() => setIsTooltipOpen(true)}
        onMouseLeave={() => setIsTooltipOpen(false)}
      >
        <div onContextMenu={rightClickHandler} className="ltr">
          <Cell clonedRow={row} headerName={headerName} type="Price" />
          {isMenuOpen ? (
            <Menu
              className="!z-[9998]"
              anchorEl={anchorEl}
              open={isMenuOpen}
              onClose={handleCloseMenu}
              MenuListProps={{
                "aria-labelledby": "basic-button",
              }}
            >
              <MenuItem onClick={getChartHandler}>نمودار</MenuItem>
            </Menu>
          ) : (
            <></>
          )}
        </div>
      </Tooltip>
    </>
  );
};

export default CancelNavGraph;
